/**
 * Resolves an asset URL to a full URL
 * Handles both relative paths (from uploads) and absolute URLs.
 * In Capacitor, builds the URL using the tenant subdomain so NGINX
 * can proxy /storage/* correctly per tenant.
 */
export function resolveAssetUrl(url: string | null | undefined): string | null {
  if (!url) return null;

  // Already a full URL
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // Ensure URL starts with /
  const path = url.startsWith('/') ? url : `/${url}`;

  // In Capacitor or localhost, build full URL using tenant subdomain
  const subdomain = localStorage.getItem('tenantSubdomain');
  if (subdomain) {
    return `https://${subdomain}.tasklic.com${path}`;
  }

  // Fallback: use API base URL without /api/v1
  const apiUrl = import.meta.env.VITE_API_URL?.replace('/api/v1', '') || 'https://app.tasklic.com';
  return `${apiUrl}${path}`;
}

/**
 * Storage URL rewritten to be same-origin, for previews.
 *
 * Attachment paths are stored ABSOLUTE at upload time (e.g.
 * `http://localhost:3333/uploads/...`), which is a different origin from
 * the app. That is fine for `<img>` and download links, but it breaks the
 * two things a file viewer needs:
 *
 *   - `<object>` / `<iframe>` PDF embedding, which cross-origin framing
 *     and `X-Frame-Options` block; and
 *   - `fetch()` of a text file, which CORS blocks.
 *
 * Both storage paths are served on the app's own origin — by the Vite
 * proxy in dev, by NGINX per tenant in production — so dropping the
 * scheme+host makes the browser ask our own origin instead. Anything that
 * isn't a recognised storage path is returned untouched.
 */
const SAME_ORIGIN_STORAGE_PREFIXES = ['/uploads/', '/storage/'];

/**
 * Signed private-file URLs (`/api/v1/files/p/<key>?e=&s=`) are what the API
 * returns for every chat attachment, TDrive file and task / project / ticket
 * attachment. They are complete as given — the signature covers the key and
 * expiry, the route sets CORS and framing headers itself — so they must
 * never be rewritten, re-rooted or composed by the client.
 */
export const PRIVATE_FILE_URL_PREFIX = '/api/v1/files/p/';

export function isSignedPrivateFileUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    return new URL(url, 'http://placeholder.invalid').pathname.startsWith(PRIVATE_FILE_URL_PREFIX);
  } catch {
    return false;
  }
}

export function sameOriginAssetUrl(url: string | null | undefined): string | null {
  const resolved = resolveAssetUrl(url);
  if (!resolved) return null;
  // Already relative — nothing to strip.
  if (!/^https?:\/\//i.test(resolved)) return resolved;
  // A signed URL is used exactly as the API gave it.
  if (isSignedPrivateFileUrl(resolved)) return resolved;
  try {
    const parsed = new URL(resolved);
    if (SAME_ORIGIN_STORAGE_PREFIXES.some((p) => parsed.pathname.startsWith(p))) {
      return `${parsed.pathname}${parsed.search}`;
    }
  } catch {
    /* not parseable — fall through and use it as-is */
  }
  return resolved;
}

/**
 * Get avatar URL with fallback
 */
export function getAvatarUrl(avatar: string | null | undefined): string | null {
  return resolveAssetUrl(avatar);
}

/**
 * Get initials from name
 */
export function getInitials(firstName?: string, lastName?: string): string {
  const first = firstName?.charAt(0) || '';
  const last = lastName?.charAt(0) || '';
  return `${first}${last}`.toUpperCase();
}
